import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Save } from 'lucide-react';
import { useAdminStore } from '@/lib/admin-store';
import AdminBilingualInput from '@/components/admin/AdminBilingualInput';
import AdminImageUpload from '@/components/admin/AdminImageUpload';
import AdminMultiImageUpload from '@/components/admin/AdminMultiImageUpload';
import toast from 'react-hot-toast';
import type { BilingualText, Artwork, Artist } from '@/types';

const INPUT_CLS = "w-full px-4 py-3 bg-black/[0.03] dark:bg-white/[0.05] border border-black/[0.06] dark:border-white/[0.08] rounded-xl text-sm font-medium outline-none focus:border-black/20 dark:focus:border-white/20 transition-all placeholder:text-black/20 dark:placeholder:text-white/20";
const LABEL_CLS = "block text-[9px] font-bold uppercase tracking-[0.2em] text-black/40 dark:text-white/40 mb-2";

const AdminArtFairEditorPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const artFairs = useAdminStore((s) => s.artFairs);
  const allArtworks = useAdminStore((s) => s.artworks);
  const addArtFair = useAdminStore((s) => s.addArtFair);
  const updateArtFair = useAdminStore((s) => s.updateArtFair);
  const fieldLimits = useAdminStore((s) => s.settings?.fieldLimits);

  const isEditing = id !== undefined && id !== 'new';
  const existing = isEditing ? artFairs.find((f) => f._id === id) : undefined;

  const [title, setTitle] = useState<BilingualText>({ en: '', ka: '' });
  const [excerpt, setExcerpt] = useState<BilingualText>({ en: '', ka: '' });
  const [description, setDescription] = useState<BilingualText>({ en: '', ka: '' });
  const [poster, setPoster] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [location, setLocation] = useState<BilingualText>({ en: '', ka: '' });
  const [images, setImages] = useState<string[]>([]);
  const [selectedArtworkIds, setSelectedArtworkIds] = useState<string[]>([]);

  useEffect(() => {
    if (existing) {
      setTitle(existing.title);
      setExcerpt(existing.excerpt || { en: '', ka: '' });
      setDescription(existing.description);
      setPoster(existing.poster);
      setStartDate(existing.startDate);
      setEndDate(existing.endDate);
      setLocation(existing.location);
      setImages(existing.images);
      setSelectedArtworkIds(existing.artworks.map((aw) => aw._id));
    }
  }, [existing]);

  const toggleArtwork = (artworkId: string): void => {
    setSelectedArtworkIds((prev) =>
      prev.includes(artworkId) ? prev.filter((aid) => aid !== artworkId) : [...prev, artworkId]
    );
  };

  const getArtistName = (artist: Artist | string): string =>
    typeof artist === 'string' ? artist : artist.name.en;

  const handleSave = (): void => {
    try {
      if (!title.en.trim()) { toast.error('Title (EN) is required'); return; }
      if (!startDate || !endDate) { toast.error('Start and end dates are required'); return; }
      const selectedArtworks: Artwork[] = allArtworks.filter((aw) => selectedArtworkIds.includes(aw._id));
      if (isEditing && existing) {
        updateArtFair(existing._id, { title, excerpt, description, poster, images, startDate, endDate, location, artworks: selectedArtworks });
        toast.success('Art fair updated successfully!');
      } else {
        addArtFair({ title, excerpt, description, poster, images, startDate, endDate, location, artworks: selectedArtworks });
        toast.success('Art fair created successfully!');
      }
      navigate('/admin/art-fairs');
    } catch (err) {
      console.error('Failed to save art fair:', err);
      toast.error('Failed to save art fair. Please try again.');
    }
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="flex items-center gap-4">
        <Link to="/admin/art-fairs" className="w-9 h-9 rounded-xl border border-black/[0.06] dark:border-white/[0.06] flex items-center justify-center text-black/30 dark:text-white/30 hover:text-black dark:hover:text-white hover:border-black/15 dark:hover:border-white/15 transition-all">
          <ArrowLeft size={16} />
        </Link>
        <div>
          <h1 className="text-2xl font-syncopate font-bold tracking-tighter">{isEditing ? 'Edit Art Fair' : 'New Art Fair'}</h1>
          <p className="mt-1 text-[10px] font-bold uppercase tracking-[0.3em] text-black/20 dark:text-white/20">{isEditing ? existing?.title.en : 'Create a new art fair'}</p>
        </div>
      </div>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }} className="bg-white dark:bg-black border border-black/[0.04] dark:border-white/[0.04] rounded-2xl p-6 space-y-5">
        <AdminBilingualInput label="Title" value={title} onChange={setTitle} placeholder="Art fair title" maxLength={fieldLimits?.title} />
        <AdminBilingualInput label="Short Description" value={excerpt} onChange={setExcerpt} placeholder="Brief description shown on listing page (1-2 sentences)" maxLength={fieldLimits?.shortDescription} />
        <AdminBilingualInput label="Full Description" value={description} onChange={setDescription} textarea placeholder="Full art fair description shown on detail page" maxLength={fieldLimits?.description} />
        <AdminImageUpload label="Poster" value={poster} onChange={setPoster} shape="rect" />
        <AdminMultiImageUpload label="Gallery Images" value={images} onChange={setImages} />
        <div className="grid grid-cols-2 gap-4">
          <div><label className={LABEL_CLS}>Start Date</label><input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className={INPUT_CLS} /></div>
          <div><label className={LABEL_CLS}>End Date</label><input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className={INPUT_CLS} /></div>
        </div>
        <AdminBilingualInput label="Location" value={location} onChange={setLocation} placeholder="Venue name, city" maxLength={fieldLimits?.shortDescription} />
        <div>
          <label className={LABEL_CLS}>Artworks</label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-1 max-h-80 overflow-y-auto pr-1">
            {allArtworks.map((aw) => (
              <label key={aw._id} className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${selectedArtworkIds.includes(aw._id) ? 'border-black/20 dark:border-white/20 bg-black/[0.03] dark:bg-white/[0.05]' : 'border-black/[0.06] dark:border-white/[0.06] hover:border-black/10 dark:hover:border-white/10'}`}>
                <input type="checkbox" checked={selectedArtworkIds.includes(aw._id)} onChange={() => toggleArtwork(aw._id)} className="w-4 h-4 rounded accent-black dark:accent-white shrink-0" />
                <div className="flex items-center gap-2 min-w-0">
                  <img src={aw.images?.[0] || ''} alt="" className="w-8 h-8 rounded-lg object-cover shrink-0" />
                  <div className="min-w-0">
                    <span className="text-[11px] font-medium truncate block">{aw.title.en}</span>
                    <span className="text-[9px] text-black/30 dark:text-white/30 truncate block">{getArtistName(aw.artist)}</span>
                  </div>
                </div>
              </label>
            ))}
          </div>
          {allArtworks.length === 0 && <p className="text-[10px] text-black/30 dark:text-white/30 mt-2">No artworks available.</p>}
        </div>
      </motion.div>
      <div className="flex items-center gap-3">
        <button onClick={handleSave} className="flex items-center gap-2 px-6 py-3 bg-black dark:bg-white text-white dark:text-black text-[10px] font-bold uppercase tracking-[0.15em] rounded-xl hover:opacity-90 transition-opacity">
          <Save size={14} /> {isEditing ? 'Save Changes' : 'Create Art Fair'}
        </button>
        <Link to="/admin/art-fairs" className="px-6 py-3 text-[10px] font-bold uppercase tracking-[0.15em] text-black/40 dark:text-white/40 hover:text-black dark:hover:text-white transition-colors">Cancel</Link>
      </div>
    </div>
  );
};

export default AdminArtFairEditorPage;
